import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface ClockContextValue {
  now: Date;
  isTimeTraveling: boolean;
  rawNowParam: string | null;
}

/** Countdown chips show minutes, so a 30s tick keeps them within half a minute of true. */
const TICK_MS = 30_000;

/**
 * `?now=` is a demo/testing aid for the Sep 30 / Oct 1 pricing switch. It rewrites what every
 * cost surface believes the time is, so it must never work for a tenant in production.
 */
const ALLOW_TIME_TRAVEL =
  import.meta.env.DEV || import.meta.env.VITE_BILLING_MOCK === "1";

const ClockContext = createContext<ClockContextValue>({
  now: new Date(),
  isTimeTraveling: false,
  rawNowParam: null,
});

/**
 * Hook to get the current time. It ADVANCES: a session left open across the Oct 1 rate change,
 * or across a window closing, re-evaluates on the next tick instead of staying frozen at mount.
 * With `?now=YYYY-MM-DDTHH:mm:ss...` (dev / mock builds only) the clock starts at that instant
 * and keeps running from it.
 */
export function useClock(): Date {
  return useContext(ClockContext).now;
}

export function useClockDetails(): ClockContextValue {
  return useContext(ClockContext);
}

export function ClockProvider({ children }: { children: ReactNode }) {
  const [search, setSearch] = useState(() =>
    typeof window !== "undefined" ? window.location.search : "",
  );
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    function handleLocationChange() {
      setSearch(window.location.search);
    }
    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // Fixed once per `?now=` value: how far the requested instant is from the real one.
  const override = useMemo(() => {
    if (!ALLOW_TIME_TRAVEL) return null;
    const rawNowParam = new URLSearchParams(search).get("now");
    if (!rawNowParam) return null;
    const requested = new Date(rawNowParam);
    if (Number.isNaN(requested.getTime())) return null;
    return { rawNowParam, offsetMs: requested.getTime() - Date.now() };
  }, [search]);

  const value = useMemo<ClockContextValue>(
    () => ({
      now: new Date(tick + (override?.offsetMs ?? 0)),
      isTimeTraveling: override !== null,
      rawNowParam: override?.rawNowParam ?? null,
    }),
    [tick, override],
  );

  return <ClockContext.Provider value={value}>{children}</ClockContext.Provider>;
}
